import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { TextField } from 'mui-rff';
import { ConfirmationDialog } from '@src/design-system/dialogs/ConfirmationDialog';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { Form } from 'react-final-form';
import { flashPackTheme } from '@src/design-system/theme';
import { TextButton, LoadingButton } from 'design-system';
import { FormParser } from '@src/design-system/forms/parsers';
import { pluralDepartures } from '@src/departures/utils';
import {
  BulkUpdateMandatoryCostsDocument,
  DepartureFinancesQueryDocument,
  ItineraryWithMandatoryCostsDocument,
  Scalars,
  SupportedCurrencySymbol,
} from '@flashpack/graphql';
import { useSafeMutation } from '@src/shared/useSafeMutation';
import { ApolloError, useMutation } from '@apollo/client';
import { useToast } from '@src/shared/toast/useToast';
import { currencyOptions } from './CurrencyDropdown';
import usePermissions from '@src/shared/usePermissions';

interface PropTypes {
  open: boolean;
  onClose: () => void;
  selectedDepartureIds: Array<Scalars['UUID']>;
  setSelectedDepartureIds?: Dispatch<SetStateAction<string[]>>;
  costCurrency?: SupportedCurrencySymbol | null;
  initialValues?: BulkUpdateMandatoryCostsModalFormType | null;
  locked?: boolean;
}

export type BulkUpdateMandatoryCostsModalFormType = {
  amountInternalFlight: number;
  amountTransfer: number;
  amountOther: number;
};

export const BulkUpdateMandatoryCostsModal: FC<PropTypes> = ({
  open,
  onClose,
  selectedDepartureIds,
  setSelectedDepartureIds,
  costCurrency,
  initialValues,
  locked = false,
  ...props
}) => {
  const { safeMutation } = useSafeMutation();
  const { canUserSubmitForm } = usePermissions();
  const [bulkUpdateMandatoryCosts] = useMutation(BulkUpdateMandatoryCostsDocument);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { error: errorToast, success: successToast } = useToast();
  const title = `Update ${pluralDepartures(selectedDepartureIds.length)}?`;
  const lockForm = !canUserSubmitForm(locked);

  const initialFormValues: Partial<BulkUpdateMandatoryCostsModalFormType> = {
    amountInternalFlight: initialValues?.amountInternalFlight,
    amountTransfer: initialValues?.amountTransfer,
    amountOther: initialValues?.amountOther,
  };

  const handleSubmitMandatoryCosts = async (
    values: BulkUpdateMandatoryCostsModalFormType,
  ) => {
    setIsSubmitting(true);
    await safeMutation(
      bulkUpdateMandatoryCosts({
        variables: {
          input: {
            departureIds: selectedDepartureIds,
            amounts: values,
          },
        },
        refetchQueries: [
          ItineraryWithMandatoryCostsDocument,
          DepartureFinancesQueryDocument,
        ],
      }),
      {
        onSuccess: () => {
          setIsSubmitting(false);
          onClose();
          successToast('Mandatory costs updated successfully');
          setSelectedDepartureIds && setSelectedDepartureIds([]);
        },
        onError: (err) => {
          setIsSubmitting(false);
          if (err instanceof ApolloError) {
            errorToast(err.message);
          } else {
            errorToast('Could not update mandatory costs');
          }
        },
      },
    );
  };

  const currencyDisplayed = currencyOptions.find(
    (option) => option.value === costCurrency,
  )?.label;

  const validateForm = (values: BulkUpdateMandatoryCostsModalFormType) => {
    const errors: { _form?: string } = {};

    if (!values.amountInternalFlight && !values.amountTransfer && !values.amountOther) {
      errors._form = 'At least one field needs to be filled';
    }

    return errors;
  };

  return (
    <ConfirmationDialog
      {...props}
      open={open}
      onCancel={onClose}
      title={title}
      maxWidth="sm"
      hasCustomActions={true}
      cancelLabel="Cancel"
      confirmLabel="Update"
    >
      <Typography variant="captionPara" color={flashPackTheme.palette.principal.grey70}>
        Mandatory Costs are in {currencyDisplayed ? currencyDisplayed : '...'}
      </Typography>
      <Form<BulkUpdateMandatoryCostsModalFormType>
        onSubmit={handleSubmitMandatoryCosts}
        initialValues={initialFormValues}
        validate={validateForm}
        render={({ handleSubmit, submitFailed, errors }) => (
          <form onSubmit={(v) => void handleSubmit(v)}>
            <Stack spacing={1} direction="row">
              <Stack direction="row" spacing={1} alignItems="center" flex={1}>
                <Tooltip title="Enter a new cost to update all selected instances">
                  <Typography variant="bodySingle" textAlign="right">
                    Internal Flights
                  </Typography>
                </Tooltip>
                <TextField
                  name="amountInternalFlight"
                  title="Enter a new cost to update all selected instances"
                  disabled={lockForm}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  fieldProps={{
                    parse: FormParser.parseNumber,
                  }}
                  sx={{
                    width: flashPackTheme.spacing(10),
                    paddingY: flashPackTheme.spacing(1),
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flex={1.1}>
                <Tooltip title="Enter a new cost to update all selected instances">
                  <Box>
                    <Typography variant="bodySingle" textAlign="right">
                      Transfer included
                    </Typography>
                    <Typography variant="captionPara" textAlign="right">
                      (per person)
                    </Typography>
                  </Box>
                </Tooltip>
                <TextField
                  name="amountTransfer"
                  title="Enter a new cost to update all selected instances"
                  disabled={lockForm}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  fieldProps={{
                    parse: FormParser.parseNumber,
                  }}
                  sx={{
                    width: flashPackTheme.spacing(10),
                    paddingY: flashPackTheme.spacing(1),
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flex={0.9}>
                <Tooltip title="Enter a new cost to update all selected instances">
                  <Typography variant="bodySingle">Other</Typography>
                </Tooltip>
                <TextField
                  name="amountOther"
                  title="Enter a new cost to update all selected instances"
                  disabled={lockForm}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  fieldProps={{
                    parse: FormParser.parseNumber,
                  }}
                  sx={{
                    width: flashPackTheme.spacing(10),
                    paddingY: flashPackTheme.spacing(1),
                  }}
                />
              </Stack>
            </Stack>
            {submitFailed && errors?._form && (
              <Typography variant="captionPara" color="error" sx={{ marginTop: 2 }}>
                {errors._form}
              </Typography>
            )}
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ marginTop: flashPackTheme.spacing(2) }}
            >
              <TextButton
                variant={'outlined'}
                sx={{ width: flashPackTheme.spacing(15) }}
                onClick={onClose}
              >
                Cancel
              </TextButton>
              <LoadingButton
                variant="contained"
                sx={{ width: flashPackTheme.spacing(18) }}
                disabled={lockForm}
                type="submit"
                data-testid="submit-bulk-update-mandatory-costs-button"
                loading={isSubmitting}
              >
                Update
              </LoadingButton>
            </Stack>
          </form>
        )}
      />
    </ConfirmationDialog>
  );
};
